// conventions-allow: file-size — one concept, error to alert: a backend code, its details and its operator
// wording change together.
import { ALERT_KEYS } from 'store/alert/alert.constants'
import { formatBidStartTimes, withoutRepeatedInstants } from './elplan.alert.helper'
import type { ElplanAlertMessage, ElplanErrorAlert } from './elplan.alert.types'
import { type ElplanErrorIdentity, getElplanErrorIdentity, isRecord } from './elplan.errorBody.helper'
import {
  readDetailsNumber,
  readDetailsString,
  readDetailsStringList,
  readDetailsStringListAllowEmpty,
  readDetailsTagListAllowEmpty,
} from './elplan.errorDetails.helper'

const DEADLINE_PASSED_CODE = 'ELPLAN_DA_SUBMISSION_DEADLINE_PASSED'

const DEADLINE_PASSED_ALERT_KEY = 'The bid deadline has passed.'

function isDeadlinePassedRefusal(code: string | undefined, legacyText: string | undefined): boolean {
  return code === DEADLINE_PASSED_CODE || (legacyText?.includes('Day ahead productionplan was sent to late') ?? false)
}

const NO_BIDS_CODE = 'ELPLAN_NO_BIDS_FOR_DATE'
const NO_BIDS_ALERT_KEY = 'No bids found for the given date.'

const TAG_COLLISION_CODE = 'ELPLAN_DA_TAG_COLLISION_FOR_SEND'
const TAG_COLLISION_UNLISTED_ALERT_KEY =
  'Nothing was sent. Several block or profile bids use the same tag. Remove or retag them and send again.'
const TAG_COLLISION_ALERT_KEY =
  'Nothing was sent for {{date}}. Several block or profile bids use the same tag ({{start_times}}). Remove or retag them and send again.'

function resolveTagCollisionAlert(details: Record<string, unknown> | undefined): ElplanErrorAlert {
  const date = readDetailsString(details, 'date')
  const startTimes = readDetailsStringList(details, 'colliding_bid_start_times')

  if (!date || !startTimes || startTimes.length === 0) {
    return { key: TAG_COLLISION_UNLISTED_ALERT_KEY, alertKey: TAG_COLLISION_CODE }
  }

  return {
    key: TAG_COLLISION_ALERT_KEY,
    values: { date, start_times: formatBidStartTimes(startTimes) },
    alertKey: TAG_COLLISION_CODE,
  }
}

const BID_INCOMPLETE_CODE = 'ELPLAN_DA_BID_INCOMPLETE_FOR_SEND'
const BID_INCOMPLETE_ALERT_KEY = 'Nothing was sent for {{date}}. Some block or profile bids are incomplete:'
const BID_INCOMPLETE_UNLISTED_ALERT_KEY =
  'Nothing was sent. Some block or profile bids are incomplete. Complete or remove them and send again.'
const BID_INCOMPLETE_REMEDY_KEY = 'Complete or remove these bids and send again.'

type FaultStartTimesSource = {
  detailsKey: string
  optional?: boolean
  read?: (value: unknown) => string[] | undefined
}
type DetailsFault = FaultStartTimesSource & { alertKey: string }

// "Unusable" volume, not "missing": the list also carries bids whose volume is present but empty or
// the wrong length. An off-the-hour bid can hold good values and still be rejected, as the day is keyed on the hour.
const BID_INCOMPLETE_FAULTS: DetailsFault[] = [
  { detailsKey: 'bids_missing_price', alertKey: 'Missing price ({{start_times}}).' },
  { detailsKey: 'bids_with_unusable_volume', alertKey: 'Missing or invalid volume ({{start_times}}).' },
  {
    detailsKey: 'bids_not_starting_on_the_hour',
    alertKey: 'Not starting on the hour ({{start_times}}).',
  },
]

const DAY_TABLE_TAG_USED_CODE = 'TAG_ALREADY_USED_FOR_DATE'
const DAY_TABLE_TAG_SET_MISMATCH_CODE = 'ELPLAN_DA_TABLE_TAG_SET_MISMATCH'
const DAY_TABLE_HOURS_MISMATCH_CODE = 'ELPLAN_DA_TABLE_HOURS_MISMATCH'

const BLOCK_PROFILE_CAP_CODE = 'MAX_BLOCK_PROFILE_BIDS_EXCEEDED'
const BID_TYPE_CAP_CODE = 'MAX_BIDS_PER_TYPE_EXCEEDED'
const DAILY_BID_CAP_ALERT_KEY = 'The day would hold more bids than allowed. Remove a bid from that day first.'

const TAG_PARTITION_MISMATCH_CODE = 'TAG_PARTITION_MISMATCH'
const TAG_PARTITION_MISMATCH_ALERT_KEY =
  'A bid uses a tag that belongs to another partition. Choose a tag of this partition and save again.'

const ID_HOUR_ALREADY_STARTED_CODE = 'ELPLAN_ID_HOUR_ALREADY_STARTED'
const ID_HOUR_ALREADY_STARTED_ALERT_KEY =
  'Nothing was sent because the table is out of date. Reload the page and send again.'

function resolveDayTableTagUsedAlert(details: Record<string, unknown> | undefined): ElplanErrorAlert | undefined {
  const date = readDetailsString(details, 'date')
  const tag = readDetailsNumber(details, 'tag')
  if (!date || !tag) return undefined
  return {
    key: 'The tag {{tag}} is already used for {{date}}. Remove or retag the existing bid before saving.',
    values: { date, tag },
    alertKey: DAY_TABLE_TAG_USED_CODE,
  }
}

function resolveDayTableTagSetMismatchAlert(
  details: Record<string, unknown> | undefined
): ElplanErrorAlert | undefined {
  const date = readDetailsString(details, 'date')
  const missingTags = readDetailsTagListAllowEmpty(details, 'missing_tags')
  const unexpectedTags = readDetailsTagListAllowEmpty(details, 'unexpected_tags')
  if (!date || !missingTags || !unexpectedTags || (missingTags.length === 0 && unexpectedTags.length === 0)) {
    return undefined
  }
  return {
    key: 'The bid table tags for {{date}} do not match the configured tags. Missing: {{missingTags}}. Unexpected: {{unexpectedTags}}.',
    values: {
      date,
      missingTags: missingTags.join(', ') || 'none',
      unexpectedTags: unexpectedTags.join(', ') || 'none',
    },
    alertKey: DAY_TABLE_TAG_SET_MISMATCH_CODE,
  }
}

function resolveDayTableHoursMismatchAlert(details: Record<string, unknown> | undefined): ElplanErrorAlert | undefined {
  const date = readDetailsString(details, 'date')
  const tag = readDetailsNumber(details, 'tag')
  const expectedHours = readDetailsStringListAllowEmpty(details, 'expected_hours')
  if (!date || !tag || !expectedHours) return undefined
  return {
    key: 'The bid table hours for tag {{tag}} do not match the configured hours for {{date}}.',
    values: { date, tag },
    alertKey: DAY_TABLE_HOURS_MISMATCH_CODE,
  }
}

function toFaultClause(alertKey: string, startTimes: string[]): ElplanAlertMessage {
  return {
    key: alertKey,
    values: { start_times: formatBidStartTimes(startTimes) },
  }
}

function readFaultStartTimes(
  details: Record<string, unknown> | undefined,
  fault: FaultStartTimesSource
): string[] | undefined {
  if (fault.optional && details?.[fault.detailsKey] === undefined) {
    return []
  }
  return fault.read ? fault.read(details?.[fault.detailsKey]) : readDetailsStringList(details, fault.detailsKey)
}

function readFaultClauses(
  details: Record<string, unknown> | undefined,
  faults: DetailsFault[]
): ElplanAlertMessage[] | undefined {
  const faultClauses: ElplanAlertMessage[] = []
  for (const fault of faults) {
    const startTimes = readFaultStartTimes(details, fault)
    if (startTimes === undefined) {
      return undefined
    }
    if (startTimes.length > 0) {
      faultClauses.push(toFaultClause(fault.alertKey, startTimes))
    }
  }
  return faultClauses
}

function resolveIncompleteBidAlert(details: Record<string, unknown> | undefined): ElplanErrorAlert {
  const date = readDetailsString(details, 'date')
  const faultParts = readFaultClauses(details, BID_INCOMPLETE_FAULTS)

  if (!date || !faultParts || faultParts.length === 0) {
    return { key: BID_INCOMPLETE_UNLISTED_ALERT_KEY, alertKey: BID_INCOMPLETE_CODE }
  }

  return {
    key: BID_INCOMPLETE_ALERT_KEY,
    values: { date },
    alertKey: BID_INCOMPLETE_CODE,
    parts: [...faultParts, { key: BID_INCOMPLETE_REMEDY_KEY }],
  }
}

const BID_INVALID_CODE = 'ELPLAN_DA_BID_INVALID'
const BID_INVALID_ALERT_KEY = 'Nothing was sent for {{date}}. Some bids are not accepted:'
const BID_INVALID_REMEDY_KEY = 'Change or remove these bids and send again.'
const BID_INVALID_UNLISTED_ALERT_KEY =
  'Nothing was sent. Some bids are not accepted. Change or remove them and send again.'

type BidInvalidReason = { alertKey: string; faults: FaultStartTimesSource[] }

const BID_INVALID_REASONS: BidInvalidReason[] = [
  {
    alertKey: 'Unsupported bid type ({{start_times}}).',
    faults: [
      { detailsKey: 'unsupported_bid_type', read: readUnsupportedBidTypeStartTimes },
      { detailsKey: 'no_known_bid_type', optional: true },
    ],
  },
  {
    alertKey: 'Invalid volume or price ({{start_times}}).',
    faults: [
      { detailsKey: 'invalid_volume' },
      { detailsKey: 'block_price' },
      { detailsKey: 'block_mixed_sign' },
      { detailsKey: 'block_not_in_eur', optional: true },
    ],
  },
  {
    alertKey: 'Invalid block hours ({{start_times}}).',
    faults: [
      { detailsKey: 'block_too_short' },
      { detailsKey: 'block_off_the_hour', optional: true },
      { detailsKey: 'block_beyond_day', optional: true },
    ],
  },
  {
    alertKey: 'Invalid time period ({{start_times}}).',
    faults: [
      { detailsKey: 'ends_before_start', optional: true },
      { detailsKey: 'off_quarter_grid', optional: true },
      { detailsKey: 'not_one_quarter', optional: true },
      { detailsKey: 'shared_quarter', optional: true },
    ],
  },
]

const BLOCK_COUNT_OVER_CONTRACT_ALERT_KEY = 'Too many block or profile bids.'

function readUnsupportedBidTypeStartTimes(entries: unknown): string[] | undefined {
  if (!Array.isArray(entries)) {
    return undefined
  }

  const startTimes: string[] = []
  for (const entry of entries) {
    const entryStartTimes = isRecord(entry) ? readDetailsStringList(entry, 'start_times') : undefined
    if (!entryStartTimes) {
      return undefined
    }
    startTimes.push(...entryStartTimes)
  }
  return startTimes
}

function readReasonStartTimes(
  details: Record<string, unknown> | undefined,
  reason: BidInvalidReason
): string[] | undefined {
  const startTimes: string[] = []
  for (const fault of reason.faults) {
    const faultStartTimes = readFaultStartTimes(details, fault)
    if (faultStartTimes === undefined) {
      return undefined
    }
    startTimes.push(...faultStartTimes)
  }
  return startTimes
}

function readBidInvalidReasonClauses(details: Record<string, unknown> | undefined): ElplanAlertMessage[] | undefined {
  const clauses: ElplanAlertMessage[] = []
  for (const reason of BID_INVALID_REASONS) {
    const startTimes = readReasonStartTimes(details, reason)
    if (!startTimes) {
      return undefined
    }
    if (startTimes.length > 0) {
      const distinctStartTimes = withoutRepeatedInstants(startTimes)
      clauses.push({ key: reason.alertKey, values: { start_times: formatBidStartTimes(distinctStartTimes) } })
    }
  }
  return clauses
}

function readBlockCountOverContractClauses(
  details: Record<string, unknown> | undefined
): ElplanAlertMessage[] | undefined {
  const counts = details?.block_count_over_contract
  if (counts === undefined) {
    return []
  }
  return isRecord(counts) ? [{ key: BLOCK_COUNT_OVER_CONTRACT_ALERT_KEY }] : undefined
}

function resolveInvalidBidAlert(details: Record<string, unknown> | undefined): ElplanErrorAlert {
  const date = readDetailsString(details, 'date')
  const reasonClauses = readBidInvalidReasonClauses(details)
  const blockCountClauses = readBlockCountOverContractClauses(details)
  const clauses = reasonClauses && blockCountClauses ? [...reasonClauses, ...blockCountClauses] : []

  if (!date || clauses.length === 0) {
    return { key: BID_INVALID_UNLISTED_ALERT_KEY, alertKey: BID_INVALID_CODE }
  }

  return {
    key: BID_INVALID_ALERT_KEY,
    values: { date },
    alertKey: BID_INVALID_CODE,
    parts: [...clauses, { key: BID_INVALID_REMEDY_KEY }],
  }
}

const ELPLAN_DETAILS_ALERT_RESOLVER_BY_CODE: Record<
  string,
  (details: Record<string, unknown> | undefined) => ElplanErrorAlert | undefined
> = {
  [TAG_COLLISION_CODE]: resolveTagCollisionAlert,
  [BID_INCOMPLETE_CODE]: resolveIncompleteBidAlert,
  [BID_INVALID_CODE]: resolveInvalidBidAlert,
  [DAY_TABLE_TAG_USED_CODE]: resolveDayTableTagUsedAlert,
  [DAY_TABLE_TAG_SET_MISMATCH_CODE]: resolveDayTableTagSetMismatchAlert,
  [DAY_TABLE_HOURS_MISMATCH_CODE]: resolveDayTableHoursMismatchAlert,
  [BLOCK_PROFILE_CAP_CODE]: () => ({ key: DAILY_BID_CAP_ALERT_KEY, alertKey: BLOCK_PROFILE_CAP_CODE }),
  [BID_TYPE_CAP_CODE]: () => ({ key: DAILY_BID_CAP_ALERT_KEY, alertKey: BID_TYPE_CAP_CODE }),
  [TAG_PARTITION_MISMATCH_CODE]: () => ({
    key: TAG_PARTITION_MISMATCH_ALERT_KEY,
    alertKey: TAG_PARTITION_MISMATCH_CODE,
  }),
  [ID_HOUR_ALREADY_STARTED_CODE]: () => ({
    key: ID_HOUR_ALREADY_STARTED_ALERT_KEY,
    alertKey: ID_HOUR_ALREADY_STARTED_CODE,
  }),
}

export type ElplanOperation = 'send' | 'withdraw' | 'fetchResults'

type ElplanFailureOutcome = 'failed' | 'outcomeUnknown'

const ELPLAN_FAILURE_OUTCOME_BY_CODE: Record<string, ElplanFailureOutcome> = {
  ELPLAN_UPSTREAM_AUTH_FAILED: 'failed',
  ELPLAN_UPSTREAM_UNREACHABLE: 'failed',
  ELPLAN_UPSTREAM_REJECTED: 'failed',
  ELPLAN_UPSTREAM_FAILED: 'failed',
  ELPLAN_UPSTREAM_TIMEOUT_DELIVERY_UNKNOWN: 'outcomeUnknown',
  ELPLAN_UPSTREAM_PARTIALLY_APPLIED: 'outcomeUnknown',
  ELPLAN_DA_SEND_FAILED: 'failed',
  ELPLAN_DA_RESULT_SYNC_FAILED: 'failed',
}

const SERVER_ERROR_STATUS = 500
const GATEWAY_TIMEOUT_STATUS = 504

function getElplanFailureOutcome(identity: ElplanErrorIdentity): ElplanFailureOutcome | undefined {
  if (identity.code) {
    return ELPLAN_FAILURE_OUTCOME_BY_CODE[identity.code]
  }
  const isUnansweredOrServerError = identity.status === undefined || identity.status >= SERVER_ERROR_STATUS
  return isUnansweredOrServerError ? 'outcomeUnknown' : undefined
}

const FETCH_RESULTS_FAILED_ALERT_KEY = 'Fetching results failed. Try again or contact us if the problem persists.'

const ELPLAN_FAILURE_ALERT_KEY_BY_OPERATION: Record<ElplanOperation, Record<ElplanFailureOutcome, string>> = {
  send: {
    failed: 'Sending the bids failed. Try again or contact us if the problem persists.',
    outcomeUnknown: 'Sending the bids may have failed. Try again or contact us if the problem persists.',
  },
  withdraw: {
    failed: 'Withdrawing the bids failed. Try again or contact us if the problem persists.',
    outcomeUnknown: 'Withdrawing the bids may have failed. Try again or contact us if the problem persists.',
  },
  fetchResults: { failed: FETCH_RESULTS_FAILED_ALERT_KEY, outcomeUnknown: FETCH_RESULTS_FAILED_ALERT_KEY },
}

type ElplanErrorAlertOptions =
  | { operation: ElplanOperation; fallback?: ElplanErrorAlert }
  | { operation: undefined; fallback: ElplanErrorAlert }

export function resolveElplanErrorAlert(error: unknown, options: ElplanErrorAlertOptions): ElplanErrorAlert {
  const identity = getElplanErrorIdentity(error)

  if (isDeadlinePassedRefusal(identity.code, identity.legacyText)) {
    return { key: DEADLINE_PASSED_ALERT_KEY, alertKey: DEADLINE_PASSED_CODE }
  }

  if (identity.code) {
    const detailsAlert = ELPLAN_DETAILS_ALERT_RESOLVER_BY_CODE[identity.code]?.(identity.details)
    if (detailsAlert) {
      return detailsAlert
    }
  }

  const outcome = getElplanFailureOutcome(identity)
  if (outcome && options.operation) {
    return {
      key: ELPLAN_FAILURE_ALERT_KEY_BY_OPERATION[options.operation][outcome],
      alertKey:
        identity.code ?? (identity.status === GATEWAY_TIMEOUT_STATUS ? ALERT_KEYS.elplanGatewayTimeout : undefined),
    }
  }

  if (identity.code === NO_BIDS_CODE || identity.legacyText?.includes(NO_BIDS_ALERT_KEY)) {
    return { key: NO_BIDS_ALERT_KEY, alertKey: NO_BIDS_CODE }
  }

  if (options.operation === undefined) {
    return options.fallback
  }
  return options.fallback ?? { key: ELPLAN_FAILURE_ALERT_KEY_BY_OPERATION[options.operation].failed }
}
